// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Summary cards component for asset rollforward report.
 *
 * Displays the key metrics in a card layout showing the asset flow:
 * Starting + Added = Total - Sales - Deployments = Ending
 */
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import {
  FixedAssetsRollforwardData,
  InventoryRollforwardData,
} from '@/report/api/types';
import {
  ASSET_TRANSITION_PHASES,
  AssetTransitionPhase,
} from '@/report/constants/asset-transition-colors';
import { RollforwardWaterfallDisplayMetricType } from '@/report/types';
import { formatCurrency } from '@/utilities/number';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Decimal } from 'decimal.js';

interface Props {
  data?: InventoryRollforwardData | FixedAssetsRollforwardData;
  metricType: RollforwardWaterfallDisplayMetricType;
  currencyCode?: IsoCurrencyCode;
}

export function AssetRollforwardSummaryCards(props: Props) {
  const theme = useTheme();

  const getValue = (item: { units: number; dollars: string | number }) => {
    if (props.metricType === 'units') {
      return new CurrencyDecimal(item.units);
    }

    return new CurrencyDecimal(item.dollars);
  };

  const formatValue = (value: Decimal, options?: { abbreviate?: boolean }) => {
    if (props.metricType === 'dollars' && options?.abbreviate === true) {
      return formatCurrency(value.toNumber(), {
        abbreviate: true,
        digits: 1,
        currencyCode: props.currencyCode,
      });
    }

    if (props.metricType === 'dollars') {
      return props.currencyCode === undefined
        ? formatCurrencyValue(null)
        : formatAmountWithCurrency(value, props.currencyCode);
    }

    return value.toNumber().toLocaleString();
  };

  const renderValueWithTooltip = (value: Decimal, color: string) => {
    if (props.metricType === 'dollars') {
      return (
        <Tooltip
          title={formatValue(value, { abbreviate: false })}
          placement="top"
          arrow
        >
          <Typography variant="h4" component="div" color={color}>
            {formatValue(value, { abbreviate: true })}
          </Typography>
        </Tooltip>
      );
    }
    return (
      <Typography variant="h4" component="div" color={color}>
        {formatValue(value)}
      </Typography>
    );
  };

  // Show skeleton cards when data is not available
  if (!props.data) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5, 6, 7].map((item) => (
          <Grid key={item} item xs={12} sm={6} md={4} lg>
            <Card sx={{ paddingX: 1 }}>
              <CardHeader
                title={<Skeleton variant="text" width="50%" height={30} />}
                sx={{ pb: 0 }}
              />
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                }}
              >
                <Typography variant="h4" component="div">
                  <Skeleton variant="text" width={40} height={50} />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <Skeleton variant="text" width={80} height={20} />
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  const isFixedAssetsData = 'depreciated' in props.data;

  // Card configuration
  const cardData: {
    phase: AssetTransitionPhase;
    value: Decimal;
  }[] = [
    {
      phase: 'start',
      value: getValue(props.data.start),
    },
    {
      phase: 'added',
      value: getValue(props.data.added),
    },
    {
      phase: 'totalAvailable',
      value: getValue(props.data.start).plus(getValue(props.data.added)),
    },
    {
      phase: isFixedAssetsData ? 'depreciated' : 'deployed',
      value: getValue(
        isFixedAssetsData
          ? (props.data as FixedAssetsRollforwardData).depreciated
          : (props.data as InventoryRollforwardData).deployed
      ),
    },
    {
      phase: 'sold',
      value: getValue(props.data.sold),
    },
    {
      phase: 'scrapped',
      value: getValue(props.data.scrapped),
    },
    {
      phase: 'end',
      value: getValue(props.data.end),
    },
  ];

  return (
    <Grid
      container
      spacing={2}
      sx={{
        margin: 0,
        width: '100%',
        '& > .MuiGrid-item:first-of-type': {
          paddingLeft: 0,
        },
        // Ensure all 7 cards are equal width on large screens
        '@media (min-width: 1200px)': {
          '& > .MuiGrid-item': {
            flex: '1 1 calc(100% / 7)',
            maxWidth: 'calc(100% / 7)',
          },
        },
      }}
    >
      {cardData.map((item) => {
        const phaseConfig = ASSET_TRANSITION_PHASES[item.phase];
        return (
          <Grid key={item.phase} item xs={12} sm={6} md={4} lg>
            <Card sx={{ paddingX: 1 }}>
              <CardHeader
                title={
                  <Tooltip
                    arrow
                    placement="top"
                    title={phaseConfig.description}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {phaseConfig.summaryTitle}
                    </Typography>
                  </Tooltip>
                }
                sx={{ pb: 1 }}
              />
              <CardContent
                sx={{
                  pt: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                }}
              >
                {renderValueWithTooltip(
                  item.value,
                  item.phase === 'depreciated' && props.metricType === 'units'
                    ? '#dddddd'
                    : phaseConfig.color(theme)
                )}
                <Typography variant="caption" color="text.secondary">
                  {props.metricType} {phaseConfig.summarySubtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
